import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from "react";
import { DEFAULT_AGENT_IMAGE, resolveWidgetAssetUrl } from "../config";
export function HomeTab({ theme, brandName, agentImage, heroTitle, heroSubtitle, welcomeMessage, t, onStart, flows, onPickFlow, latestConversation, onOpenLatest, }) {
    const isDarkColor = (value) => {
        if (!value)
            return false;
        const color = value.trim().toLowerCase();
        const hex = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
        if (hex) {
            const raw = hex[1];
            const full = raw.length === 3 ? raw.split("").map((ch) => `${ch}${ch}`).join("") : raw;
            const r = parseInt(full.slice(0, 2), 16);
            const g = parseInt(full.slice(2, 4), 16);
            const b = parseInt(full.slice(4, 6), 16);
            return (r * 299 + g * 587 + b * 114) / 1000 < 140;
        }
        const rgb = color.match(/^rgba?\(([^)]+)\)$/);
        if (rgb) {
            const [r, g, b] = rgb[1].split(",").map((part) => Number(part.trim())).slice(0, 3);
            if ([r, g, b].every((channel) => Number.isFinite(channel))) {
                return (r * 299 + g * 587 + b * 114) / 1000 < 140;
            }
        }
        return false;
    };
    const textColor = theme.primaryTextColor || theme.contentColor || theme.foreground;
    const muted = theme.secondaryTextColor || "#64748b";
    const cardBg = theme.cardBackgroundColor || theme.secondaryBackground || "#ffffff";
    const sectionBg = theme.background || "#f4f7fb";
    const activeCardBg = theme.activeCardBackgroundColor || "#1f2937";
    const gradientBase = theme.homepageGradientBaseColor || "#d9e1ec";
    const darkSurface = isDarkColor(theme.background);
    const cardShadow = darkSurface ? "0 10px 24px rgba(0,0,0,0.45)" : "0 10px 24px #dbe3ee";
    const flowCardShadow = darkSurface ? "0 5px 14px rgba(0,0,0,0.34)" : "0 5px 12px #e2e8f0";
    const hoverShadow = darkSurface ? "0 8px 18px rgba(0,0,0,0.4)" : "0 8px 18px #dbe3ee";
    const ctaLabel = brandName.trim().length > 14 ? "Let's Chat" : `Chat with ${brandName}`;
    const avatarSource = resolveWidgetAssetUrl(agentImage) || resolveWidgetAssetUrl(DEFAULT_AGENT_IMAGE) || DEFAULT_AGENT_IMAGE;
    const [hoveredFlowId, setHoveredFlowId] = useState(null);
    return (_jsxs("div", { style: {
            padding: 18,
            display: "grid",
            gap: 14,
            alignContent: "start",
            gridAutoRows: "max-content",
            color: textColor,
            background: sectionBg,
            position: "relative",
        }, children: [_jsx("div", { "aria-hidden": true, style: {
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "70%",
                    background: `linear-gradient(180deg, ${gradientBase} 0%, ${sectionBg} 100%)`,
                    pointerEvents: "none",
                    zIndex: 0,
                } }), _jsxs("div", { style: {
                    display: "grid",
                    gap: 8,
                    position: "relative",
                    zIndex: 1,
                    marginTop: 18,
                    maxHeight: "min(30vh, 168px)",
                    overflow: "hidden",
                }, children: [_jsx("div", { style: {
                            fontSize: 20,
                            fontWeight: 800,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }, children: heroTitle }), _jsx("div", { style: {
                            fontSize: 14,
                            color: muted,
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }, children: heroSubtitle })] }), _jsxs("div", { style: {
                    border: "none",
                    borderRadius: 16,
                    background: cardBg,
                    padding: 14,
                    display: "grid",
                    gap: 12,
                    boxShadow: cardShadow,
                    position: "relative",
                    zIndex: 1,
                }, children: [_jsx("div", { style: { fontSize: 16, fontWeight: 700, color: textColor }, children: brandName }), _jsxs("div", { style: { display: "flex", alignItems: "flex-start", gap: 10 }, children: [_jsx("img", { src: avatarSource, alt: brandName, style: { width: 38, height: 38, borderRadius: "50%", objectFit: "cover", flexShrink: 0 } }), _jsx("div", { style: { fontSize: 14, lineHeight: 1.5, color: textColor }, children: welcomeMessage })] }), _jsxs("button", { onClick: onStart, style: {
                            padding: "11px 14px",
                            borderRadius: 12,
                            border: `1px solid ${activeCardBg}`,
                            background: activeCardBg,
                            fontWeight: 800,
                            cursor: "pointer",
                            width: "100%",
                            color: "#ffffff",
                            textAlign: "center",
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                        }, children: [_jsx("span", { children: ctaLabel }), _jsxs("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, style: { marginLeft: 8, verticalAlign: "text-bottom" }, children: [_jsx("path", { d: "M5 12h14", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round" }), _jsx("path", { d: "M13 6l6 6-6 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" })] })] })] }), latestConversation && (_jsxs("div", { style: {
                    border: "none",
                    borderRadius: 16,
                    padding: 12,
                    background: cardBg,
                    display: "grid",
                    gap: 6,
                    cursor: onOpenLatest ? "pointer" : "default",
                    transition: "border 0.2s ease, background 0.2s ease",
                    position: "relative",
                    zIndex: 1,
                }, onClick: onOpenLatest, role: onOpenLatest ? "button" : undefined, tabIndex: onOpenLatest ? 0 : undefined, onKeyDown: (e) => {
                    if (!onOpenLatest)
                        return;
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpenLatest();
                    }
                }, children: [_jsxs("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }, children: [_jsx("div", { style: { fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: muted }, children: t("home.latestConversation") }), _jsx("div", { style: { fontSize: 12, color: muted, flexShrink: 0 }, children: latestConversation.timestamp })] }), _jsx("div", { style: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }, children: _jsx("div", { style: { fontWeight: 700, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }, children: latestConversation.title }) }), _jsx("div", { style: {
                            fontSize: 13,
                            color: muted,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }, children: latestConversation.excerpt })] })), _jsx("div", { style: {
                    display: "grid",
                    gap: 10,
                    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                    position: "relative",
                    zIndex: 1,
                }, children: flows.map((flow) => (_jsxs("div", { role: "button", tabIndex: 0, onClick: () => onPickFlow(flow), onMouseEnter: () => setHoveredFlowId(flow.id), onMouseLeave: () => setHoveredFlowId(null), onKeyDown: (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            onPickFlow(flow);
                        }
                    }, style: {
                        padding: 12,
                        borderRadius: 12,
                        border: "none",
                        background: cardBg,
                        fontWeight: 600,
                        color: textColor,
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 10,
                        transition: "transform 0.16s ease, box-shadow 0.16s ease",
                        transform: hoveredFlowId === flow.id ? "scale(1.02)" : "scale(1)",
                        boxShadow: hoveredFlowId === flow.id ? hoverShadow : flowCardShadow,
                    }, children: [_jsx("span", { style: {
                                fontSize: 13,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                flex: 1,
                            }, children: flow.label }), _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", "aria-hidden": true, style: { flexShrink: 0 }, children: _jsx("path", { d: "M9 6l6 6-6 6", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round" }) })] }, flow.id))) })] }));
}
